/** Hierarchical TanStack Query keys — API_MAPPING.md Part VII */

export const queryKeys = {
  admin: {
    overview: ['admin', 'overview'] as const,
    dashboard: ['admin', 'dashboard'] as const,
    tenants: ['admin', 'tenants'] as const,
    tenantDetail: (tenantId: string) => ['admin', 'tenants', tenantId, 'detail'] as const,
    tenantCrawlStatus: (tenantId: string) => ['admin', 'tenants', tenantId, 'crawl-status'] as const,
    tenantWhatsappConfig: (tenantId: string) => ['admin', 'tenants', tenantId, 'whatsapp-config'] as const,
    tenantWhatsappSession: (tenantId: string) => ['admin', 'tenants', tenantId, 'whatsapp-session'] as const,
    tenantDocuments: (tenantId: string) => ['admin', 'tenants', tenantId, 'documents'] as const,
    tenantRagProfile: (tenantId: string) => ['admin', 'tenants', tenantId, 'rag-profile'] as const,
    tenantWidgetCode: (tenantId: string) => ['admin', 'tenants', tenantId, 'widget-code'] as const,
    tenantUsers: (tenantId: string) => ['admin', 'tenants', tenantId, 'users'] as const,
    plans: ['admin', 'plans'] as const,
    crawlSummary: ['admin', 'crawl', 'summary'] as const,
    handoffSummary: (tenantId?: string) => ['admin', 'whatsapp', 'handoff-summary', { tenantId }] as const,
    activityLogFilters: (filters: Record<string, unknown>) => ['admin', 'activity-log', filters] as const,
    qualityLiveScores: (filters?: Record<string, unknown>) =>
      ['admin', 'quality', 'live-scores', filters ?? {}] as const,
    qualityFailurePatterns: (tenantId?: string) =>
      ['admin', 'quality', 'failure-patterns', { tenantId }] as const,
    qualityCandidates: (status?: string) => ['admin', 'quality', 'candidates', { status }] as const,
    industryConfig: (key: string) => ['admin', 'industry-configs', key] as const,
    industryAudit: ['admin', 'industry-configs', 'audit'] as const,
    crmAdminStats: ['admin', 'crm', 'admin-stats'] as const,
    deliveryHealth: (days: number) => ['admin', 'whatsapp', 'delivery-health', { days }] as const,
    tenantsRecent: (days: number) => ['admin', 'tenants', 'recent', { days }] as const,
    activityLog: (limit: number) => ['admin', 'activity-log', { limit }] as const,
    qualityOverview: (days: number) => ['admin', 'quality', 'overview', { days }] as const,
    jobRuns: ['admin', 'job-runs', 'recent'] as const,
    industryConfigs: ['admin', 'industry-configs'] as const,
    platformOverview: ['admin', 'platform-overview'] as const,
    quality: {
      root: ['admin', 'quality'] as const,
      reviewQueue: ['admin', 'quality', 'review-queue'] as const,
      pipelineStatus: ['admin', 'quality', 'pipeline-status'] as const,
    },
    paynow: {
      pending: ['admin', 'paynow', 'pending'] as const,
    },
    /* Phase 5.1: Voice AI Platform */
    voiceOverview: ['admin', 'voice', 'overview'] as const,
    voiceLiveCalls: ['admin', 'voice', 'live-calls'] as const,
    voiceTenantUsage: ['admin', 'voice', 'tenant-usage'] as const,
    voiceProviderHealth: ['admin', 'voice', 'provider-health'] as const,
    voiceDashboard: ['admin', 'voice', 'dashboard'] as const,
    me: ['admin', 'me'] as const,
    nav: ['admin', 'nav'] as const,
  },
  tenant: {
    me: (tenantId: string) => ['tenant', tenantId, 'me'] as const,
    stats: (tenantId: string) => ['tenant', tenantId, 'stats'] as const,
    conversations: (tenantId: string, filters?: Record<string, unknown>) =>
      ['tenant', tenantId, 'conversations', filters ?? {}] as const,
    conversationThread: (tenantId: string, threadType: string, threadId: string) =>
      ['tenant', tenantId, 'conversation', threadType, threadId] as const,
    crm: {
      followUps: (tenantId: string, filters?: Record<string, unknown>) =>
        ['tenant', tenantId, 'crm', 'follow-ups', filters ?? {}] as const,
      leads: (tenantId: string, filters?: Record<string, unknown>) =>
        ['tenant', tenantId, 'crm', 'leads', filters ?? {}] as const,
      appointments: (tenantId: string, filters?: Record<string, unknown>) =>
        (filters
          ? (['tenant', tenantId, 'crm', 'appointments', filters] as const)
          : (['tenant', tenantId, 'crm', 'appointments'] as const)),
      contacts: (tenantId: string, filters?: Record<string, unknown>) =>
        ['tenant', tenantId, 'crm', 'contacts', filters ?? {}] as const,
      contact: (tenantId: string, contactId: string) =>
        ['tenant', tenantId, 'crm', 'contacts', contactId] as const,
      contactTimeline: (tenantId: string, contactId: string) =>
        ['tenant', tenantId, 'crm', 'contacts', contactId, 'timeline'] as const,
      templates: (tenantId: string) => ['tenant', tenantId, 'crm', 'templates'] as const,
      deals: (tenantId: string) => ['tenant', tenantId, 'crm', 'deals'] as const,
      stats: (tenantId: string) => ['tenant', tenantId, 'crm', 'stats'] as const,
    },
    botHealth: (tenantId: string, days?: number) =>
      ['tenant', tenantId, 'bot-health', { days: days ?? 7 }] as const,
    unansweredQueries: (tenantId: string, filters?: Record<string, unknown>) =>
      ['tenant', tenantId, 'unanswered-queries', filters ?? {}] as const,
    activities: (tenantId: string, filters?: Record<string, unknown>) =>
      ['tenant', tenantId, 'activities', filters ?? {}] as const,
    chatLeads: (tenantId: string) => ['tenant', tenantId, 'chat-leads'] as const,
    trialStatus: (tenantId: string) => ['tenant', tenantId, 'trial'] as const,
    calendarStatus: (tenantId: string) => ['tenant', tenantId, 'calendar', 'status'] as const,
    calendarAvailability: (tenantId: string) => ['tenant', tenantId, 'calendar', 'availability'] as const,
    pendingActions: (tenantId: string, limit: number) =>
      ['tenant', tenantId, 'crm', 'pending-actions', { limit }] as const,
    homeConversations: (tenantId: string) =>
      ['tenant', tenantId, 'conversations', { home: true, limit: 100 }] as const,
    dashboard: (tenantId: string) => ['tenant', tenantId, 'dashboard'] as const,
    billing: {
      plans: ['tenant', 'billing', 'plans'] as const,
    },
    emailSettings: (tenantId: string) => ['tenant', tenantId, 'email-settings'] as const,
    nav: ['tenant', 'nav'] as const,
    channels: {
      apiKeys: (tenantId: string) => ['tenant', tenantId, 'channels', 'api-keys'] as const,
      whatsappConfig: (tenantId: string) => ['tenant', tenantId, 'channels', 'whatsapp', 'config'] as const,
      whatsappHealth: (tenantId: string) => ['tenant', tenantId, 'channels', 'whatsapp', 'health'] as const,
    },
    whatsappFlow: (tenantId: string) => ['tenant', tenantId, 'whatsapp-flow'] as const,
    knowledge: {
      documents: (tenantId: string) => ['tenant', tenantId, 'knowledge', 'documents'] as const,
      readiness: (tenantId: string, opts?: { refresh?: boolean }) =>
        ['tenant', tenantId, 'knowledge', 'readiness', opts ?? {}] as const,
      tenantStatus: (tenantId: string) => ['tenant', tenantId, 'knowledge', 'tenant-status'] as const,
    },
  },
  voice: {
    sessions: (tenantId: string, includeTurns?: boolean) =>
      ['voice', tenantId, 'sessions', { includeTurns: Boolean(includeTurns) }] as const,
    status: ['voice', 'status'] as const,
  },
} as const;
